import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import {
  CalendarMonth,
  Google,
  Microsoft,
  Apple,
  ContentCopy,
  CheckCircle,
  Refresh,
  Security
} from '@mui/icons-material';
import { calendarService } from '../../services/calendarService';
import { extractApiErrorMessage } from '../../utils/apiUtils';

interface CalendarTokenResponse {
  token: string;
  calendarUrl: string;
  instructions: {
    googleCalendar: string;
    outlook: string;
    appleCalendar: string;
  };
}

const CalendarSubscriptionTab: React.FC = () => {
  const [tokenData, setTokenData] = useState<CalendarTokenResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    loadCalendarToken();
  }, []);

  const loadCalendarToken = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarService.getCalendarToken();
      setTokenData(response);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to load calendar subscription information'));
      console.error('Error loading calendar token:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setSuccess(`${fieldName} copied to clipboard!`);
      setTimeout(() => {
        setCopiedField(null);
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const handleRevokeToken = async () => {
    setLoading(true);
    setError(null);
    try {
      await calendarService.revokeCalendarToken();
      setTokenData(null);
      setSuccess('Calendar token revoked successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to revoke calendar token'));
      console.error('Error revoking calendar token:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNewToken = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarService.generateNewCalendarToken();
      setTokenData(response);
      setSuccess('New calendar token generated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to generate new calendar token'));
      console.error('Error generating calendar token:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !tokenData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Calendar Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Subscribe to your appointments in external calendar applications like Google Calendar, Outlook, or Apple Calendar.
        Your calendar will automatically sync with your MindShare appointments.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {tokenData ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Your Calendar Subscription URL
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={tokenData.calendarUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Tooltip title="Copy URL">
                        <IconButton
                          onClick={() => handleCopyToClipboard(tokenData.calendarUrl, 'Calendar URL')}
                          edge="end"
                        >
                          {copiedField === 'Calendar URL' ? <CheckCircle /> : <ContentCopy />}
                        </IconButton>
                      </Tooltip>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  How to Add to Your Calendar
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Google color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Google Calendar"
                      secondary="Go to Google Calendar → Settings → Add calendar → From URL → Paste the URL above"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Microsoft color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Microsoft Outlook"
                      secondary="Go to Outlook Calendar → Add calendar → Subscribe from web → Paste the URL above"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Apple color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Apple Calendar"
                      secondary="Open Calendar app → File → New Calendar Subscription → Paste the URL above"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Calendar Settings
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleGenerateNewToken}
                  disabled={loading}
                  fullWidth
                >
                  Generate New Token
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRevokeToken}
                  disabled={loading}
                  fullWidth
                >
                  Revoke Access
                </Button>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Security:</strong> Keep your calendar URL private. If you suspect it's been compromised, 
                  revoke access and generate a new token.
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <CalendarMonth sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Calendar Subscription
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Generate a calendar token to start syncing your appointments with external calendar applications.
              </Typography>
              <Button
                variant="contained"
                startIcon={<CalendarMonth />}
                onClick={handleGenerateNewToken}
                disabled={loading}
                size="large"
              >
                Generate Calendar Token
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CalendarSubscriptionTab;
